import * as React from 'react';
import { useState } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    CircularProgress,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import { useDataProvider } from 'react-admin';

/**
 * Config-driven list of downloadable report templates for a timesheet
 * week. Adding a new template later is just adding an entry here — no
 * other changes needed in this component.
 *
 * `endpoint` is relative to the (implicitly production-scoped —
 * HttpClient prepends the current production automatically, same as
 * every other resource in this app) timesheet week's base path, matching
 * the routes registered in ProductionAdmin\TimesheetWeekPaymentsController.
 */
const REPORT_TEMPLATES = [
    { value: 'standard', label: 'Standard Template', endpoint: 'payments/export' },
    { value: 'sd', label: 'SD Template', endpoint: 'payroll-export' },
];

/**
 * Triggers an actual browser save-to-disk for a File object. dataProvider's
 * getFile() (basicRestProvider) returns { data: File, filename } but
 * doesn't itself trigger a download — this is the generic
 * createObjectURL + synthetic-click part.
 */
const saveFile = (file, filename) => {
    const blobUrl = window.URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
};

export const TimesheetWeekExportDialog = ({ open, onClose, week }) => {
    const dataProvider = useDataProvider();

    const [payrollId, setPayrollId] = useState('all');
    const [template, setTemplate] = useState(REPORT_TEMPLATES[0].value);
    const [payrolls, setPayrolls] = useState([]);
    const [loadingPayrolls, setLoadingPayrolls] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState(null);

    React.useEffect(() => {
        if (!open) return;

        setLoadingPayrolls(true);
        setError(null);

        // Bare resource name, matching <Resource name="payrolls">
        // elsewhere in App.js — HttpClient scopes this to the current
        // production automatically, same as every other resource call.
        dataProvider
            .getList('payrolls', {
                filter: {},
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'name', order: 'ASC' },
            })
            .then(({ data }) => setPayrolls(data))
            .catch(() => setError('Failed to load payrolls'))
            .finally(() => setLoadingPayrolls(false));
    }, [open, dataProvider]);

    const handleDownload = async () => {
        const templateConfig = REPORT_TEMPLATES.find((t) => t.value === template);

        const query = new URLSearchParams();
        if (payrollId !== 'all') {
            query.set('payroll_id', payrollId);
        }

        const resource = `timesheet-weeks/${week.id}/${templateConfig.endpoint}${
            query.toString() ? `?${query.toString()}` : ''
        }`;

        setDownloading(true);
        setError(null);

        try {
            const { data: file, filename } = await dataProvider.getFile(resource);
            saveFile(file, filename || `${templateConfig.value}-week-${week.id}.xlsx`);
            onClose();
        } catch (e) {
            setError('Download failed — please try again');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Export Payments</DialogTitle>
            <DialogContent>
                <FormControl fullWidth margin="normal">
                    <InputLabel id="export-payroll-label">Payroll</InputLabel>
                    <Select
                        labelId="export-payroll-label"
                        value={payrollId}
                        onChange={(e) => setPayrollId(e.target.value)}
                        disabled={loadingPayrolls}
                    >
                        <MenuItem value="all">All Payrolls</MenuItem>
                        {payrolls.map((p) => (
                            <MenuItem key={p.id} value={p.id}>
                                {p.code ? `${p.code} — ${p.name}` : p.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <InputLabel id="export-template-label">Template</InputLabel>
                    <Select
                        labelId="export-template-label"
                        value={template}
                        onChange={(e) => setTemplate(e.target.value)}
                    >
                        {REPORT_TEMPLATES.map((t) => (
                            <MenuItem key={t.value} value={t.value}>
                                {t.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {error && <p style={{ color: '#c62828', marginBottom: 0 }}>{error}</p>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={downloading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleDownload}
                    color="primary"
                    variant="contained"
                    disabled={downloading}
                    startIcon={downloading ? <CircularProgress size={16} /> : <GetAppIcon />}
                >
                    {downloading ? 'Downloading…' : 'Download'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/**
 * Row-level trigger button for TimesheetWeekList's Datagrid, opening the
 * dialog above for that specific week. `record` is auto-injected by
 * react-admin's Datagrid, matching WeekReportButton's existing usage —
 * no other wiring needed to drop this straight into the Datagrid.
 */
export const TimesheetWeekExportButton = ({ record }) => {
    const [open, setOpen] = useState(false);

    if (!record) return null;

    return (
        <>
            <Button size="small" onClick={() => setOpen(true)} style={{ textTransform: 'uppercase' }}>
                Export
            </Button>
            <TimesheetWeekExportDialog open={open} onClose={() => setOpen(false)} week={record} />
        </>
    );
};
